import {
  createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { CONFIG } from '~/_shared/constants/config';
import { Page } from '~/_shared/constants/routes';
import { useOnMount } from '~/_shared/hooks/useOnMount';
import { isPresentationalMapByPathname } from '~/_shared/presentationMode/presentationModeContext';
import { askToRefreshSalesforceWrapper } from '~/_shared/utils/3rdPartyIntegrations/salesforce/wrapper/salesforceWrapper.helpers';
import { delay } from '~/_shared/utils/delay';
import { useLatest } from '~/_shared/utils/hooks/useLatest';
import { type CancellablePromise } from '~/_shared/utils/types/common.type';
import {
  type AuthSourceData, AuthSources,
} from '~/authentication/authContext/auth.types';
import { removeUserWasDisabled } from '~/clientTeamManagement/clientTeamManagement.helpers';
import { useModal } from '~/modal/useModal.hook';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { authenticateUser } from '~/store/userData/repository/userData.repository';
import { userGetDataRequest } from '~/store/userData/userData.actionCreators';
import { useIsUserSignedInSelector } from '~/store/userData/userData.selectors';
import { useGetCurrentPageInfo } from '~/topBar/navigation/navigation.helpers';
import { ImpersonationService } from '../impersonation';
import { IMPERSONATION_TOKEN_EXP_KEY } from '../impersonation/impersonationService';
import {
  ACCESS_TOKEN_EXP_KEY, AUTH_SOURCE_KEY, AuthBroadcastChannel, type AuthMessage, frontendLogoutActions,
  getExternalAuthSourceData, initializeUser, isStoredRefreshTokenValid, REFRESH_TOKEN_KEY, RefreshTokenErrorCodes,
  refreshTokens, removeAuthTokens,
} from './auth.utils';

// Check for token validity each second
// In case device wakes from sleep and the token is expired
// We need to check validity as soon as possible
const tokenCheckIntervalInMs = 1000;

export type AuthContextType = {
  setAuthTokens: (refreshToken: string, accessExpireIn: string, authSource?: AuthSourceData) => void;
  removeAuthTokens: () => void;
  error: string | null;
  clearError: () => void;
};

export const AuthContext = createContext<AuthContextType>({
  setAuthTokens: () => undefined,
  removeAuthTokens: () => undefined,
  error: null,
  clearError: () => undefined,
});

export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const mapId = useMapIdSelector() ?? undefined;
  const currentPage = useGetCurrentPageInfo().page;
  const [refreshPromise, setRefreshPromise] = useState<CancellablePromise<boolean>>();
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const isViewPresentational = useMemo(() => isPresentationalMapByPathname(location.pathname), [location]);
  const { openModal: openImpersonationModal } = useModal('ImpersonationChanged');
  const isUserSignedIn = useIsUserSignedInSelector();

  const refreshExternalAuthToken = useCallback((authSourceData: AuthSourceData) => {
    if (authSourceData.source === AuthSources.Salesforce) {
      if (authSourceData.targetOrigin) {
        askToRefreshSalesforceWrapper(
          authSourceData.targetOrigin,
          currentPage === Page.ADMIN_MAP ? mapId : undefined,
        );
      }
      else {
        console.error('Target origin is required to refresh Salesforce token');
      }
    }
  }, [currentPage, mapId]);

  const refreshCallback = useCallback(async () => {
    ImpersonationService.checkExpiration();
    if (!isStoredRefreshTokenValid()) {
      const externalAuthSourceData = getExternalAuthSourceData();

      if (externalAuthSourceData) {
        refreshExternalAuthToken(externalAuthSourceData);
      }
      else {
        frontendLogoutActions();
      }

      return false;
    }

    setError(null);

    try {
      await refreshTokens();
    }
    catch (error) {
      if (error?.errorCode === RefreshTokenErrorCodes.INVALID_TOKEN) {
        AuthBroadcastChannel.postMessage({ type: 'logout', errorCode: RefreshTokenErrorCodes.INVALID_TOKEN });
        setError(RefreshTokenErrorCodes.INVALID_TOKEN);
        frontendLogoutActions();
      }
      else {
        console.error(error);
      }

      return false;
    }

    const delayPromise = delay(tokenCheckIntervalInMs).then(refreshCallback);
    setRefreshPromise(delayPromise);

    return true;
  }, [refreshExternalAuthToken]);

  const setAuthTokens: AuthContextType['setAuthTokens'] = useCallback((newRefreshToken, accessExpireIn, authSource) => {
    setError(null);
    localStorage.removeItem(IMPERSONATION_TOKEN_EXP_KEY);
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
    localStorage.setItem(ACCESS_TOKEN_EXP_KEY, accessExpireIn);
    if (authSource) {
      localStorage.setItem(AUTH_SOURCE_KEY, JSON.stringify(authSource));
    }
    else {
      localStorage.removeItem(AUTH_SOURCE_KEY);
    }

    const delayPromise = delay(tokenCheckIntervalInMs).then(refreshCallback);

    setRefreshPromise(delayPromise);
  }, [refreshCallback]);

  const loginWithHardcodedCredentials = useCallback(async () => {
    if (CONFIG.HARDCODED_CREDENTIALS) {
      const { email, password } = CONFIG.HARDCODED_CREDENTIALS;
      const rememberMe = true;

      const loginResponse = await authenticateUser(email, password, rememberMe);
      if (loginResponse.refresh && loginResponse.access_token_expiration) {
        setAuthTokens(loginResponse.refresh, loginResponse.access_token_expiration);
        await initializeUser();
      }
    }
  }, [setAuthTokens]);

  const initialize = useCallback(async () => {
    try {
      if (isViewPresentational) {
        return;
      }

      if (CONFIG.HARDCODED_CREDENTIALS) {
        await loginWithHardcodedCredentials();
        return;
      }

      if (!isStoredRefreshTokenValid()) {
        return;
      }

      const isSuccess = await refreshCallback();
      if (isSuccess) {
        await initializeUser();
      }
    }
    catch (e) {
      console.error(e);
    }
    finally {
      setIsInitializing(false);
    }
  }, [isViewPresentational, refreshCallback, loginWithHardcodedCredentials]);
  const initializeRef = useLatest(initialize);

  const onMessageBroadcast = useCallback(({ data }: MessageEvent<AuthMessage>) => {
    if (isViewPresentational) {
      return;
    }

    switch (data.type) {
      case 'logout': {
        if (data.errorCode) {
          setError(data.errorCode);
        }

        frontendLogoutActions();
        break;
      }
      case 'impersonationChanged': {
        openImpersonationModal({
          reason: data.reason,
          requireRefresh: true,
        });
        break;
      }
      case 'licenseUpdated': {
        dispatch(userGetDataRequest());
        break;
      }
      default:
        break;
    }
  }, [dispatch, isViewPresentational, openImpersonationModal]);

  useEffect(() => {
    AuthBroadcastChannel.addEventListener('message', onMessageBroadcast);

    return () => AuthBroadcastChannel.removeEventListener('message', onMessageBroadcast);
  }, [onMessageBroadcast]);

  const onTabVisibilitychange = useCallback(() => {
    if (document.visibilityState === 'visible' && !isUserSignedIn) {
      initializeRef.current();
    }
  }, [initializeRef, isUserSignedIn]);

  useEffect(() => {
    document.addEventListener('visibilitychange', onTabVisibilitychange);

    return () => document.removeEventListener('visibilitychange', onTabVisibilitychange);
  }, [onTabVisibilitychange]);

  useOnMount(() => {
    initialize();
  });

  useEffect(() => {
    return () => refreshPromise?.cancel();
  }, [refreshPromise]);

  const clearError = useCallback(() => {
    setError(null);
    removeUserWasDisabled();
  }, []);

  const value = useMemo(() => ({
    setAuthTokens,
    removeAuthTokens,
    error,
    clearError,
  }), [clearError, setAuthTokens, error]);

  return (
    <AuthContext.Provider value={value}>
      {isInitializing ? <OverlayLoaderComponent /> : children}
    </AuthContext.Provider>
  );
};
